import type { FC, ReactNode } from 'react'

import { MaskLines, Reveal } from '@/components/motion/reveal'
import { HeroField } from '@/components/motion/hero-field'
import { ProximityText } from '@/components/motion/proximity-text'
import { ScrambleText } from '@/components/motion/scramble-text'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { ToneSection } from '@/components/generals/tone'
import { OfficeClocks } from '@/components/generals/office-clocks'
import { ContactForm } from '@/components/landing/contact/contact-form'
import { guarantees } from '@/constants/content'
import { siteConfig } from '@/constants/site'

const Contact: FC = (): ReactNode => {
    return (
        <Layout tone='void' footerCta={false}>
            <Seo
                title='Contact'
                description='Book a call with the nSure.ai team. Share your business details and the right person from our team will be in touch.'
            />

            <ToneSection tone='void' id='contact' className='isolate overflow-hidden px-5 pb-28 pt-32 md:px-8 lg:px-12 lg:pb-40 lg:pt-44'>
                <HeroField className='lg:right-1/2' />

                <div className='grid gap-16 lg:grid-cols-12 lg:gap-10'>
                    <div className='lg:col-span-5'>
                        <div className='flex flex-col gap-10 lg:sticky lg:top-32'>
                            <span className='t-label text-tone-muted'>
                                <ScrambleText text='Book a call' delay={200} />
                            </span>

                            <MaskLines
                                as='h1'
                                immediate
                                delay={0.1}
                                className='font-heading text-[clamp(4rem,10vw,10rem)] font-semibold uppercase leading-[0.8] tracking-[-0.05em] wdth-62'
                                lines={[<ProximityText key='get' text='Get in' minWidth={62} maxWidth={125} />, <span key='touch' className='text-tone-accent'><ProximityText text='touch.' minWidth={62} maxWidth={125} /></span>]}
                            />

                            <Reveal className='t-lead max-w-md text-tone-muted' delay={0.3}>
                                Share your business details below and the right person from our team will be in touch.
                            </Reveal>

                            <Reveal className='flex flex-col gap-8 border-t border-tone-line pt-8' delay={0.4}>
                                <div className='flex flex-col gap-2'>
                                    <span className='t-label text-tone-muted'>
                                        Email
                                    </span>
                                    <a href={`mailto:${siteConfig.email}`} className='link-line self-start text-xl font-semibold'>
                                        {siteConfig.email}
                                    </a>
                                </div>
                                <div className='flex flex-col gap-3'>
                                    <span className='t-label text-tone-muted'>
                                        Offices, local time
                                    </span>
                                    <OfficeClocks layout='stack' className='max-w-xs' />
                                </div>
                            </Reveal>

                            <Reveal delay={0.5}>
                                <ul className='grid grid-cols-2 gap-px bg-tone-line'>
                                    {guarantees.map(item => (
                                        <li key={item.index} className='flex flex-col gap-1 bg-tone-bg py-4 pr-4'>
                                            <span className='font-heading text-3xl font-semibold tracking-tight wdth-75'>
                                                {item.value}
                                            </span>
                                            <span className='t-label text-tone-muted'>
                                                {item.title}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </Reveal>
                        </div>
                    </div>

                    <div className='lg:col-span-6 lg:col-start-7 lg:pt-14'>
                        <ContactForm source='Contact page' />
                    </div>
                </div>
            </ToneSection>
        </Layout>
    )
}

export default Contact
